import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import AffiliatedCompany from "../model/AffiliatedCompany";
import McmAvailableField from "../model/McmAvailableField";
import { McmAuditLogWrapper } from "../model/McmAuditEntry";
import CompanyAffiliationListRequest from "../request/CompanyAffiliationListRequest";
import McmAvailableFieldsRequest from "../request/McmAvailableFieldsRequest";
import McmAuditLogRequest from "../request/McmAuditLogRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

// --- Company Affiliation List ---

export const CompanyAffiliationListApiService = async (
  request: CompanyAffiliationListRequest,
): Promise<AffiliatedCompany[]> => {
  try {
    const url = "/company-affiliations/";
    const { data } = await axios.get(url, { params: request });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useCompanyAffiliationListApiService = (
  request: CompanyAffiliationListRequest,
) =>
  useQuery<AffiliatedCompany[], Error | AxiosError>(
    ["CompanyAffiliationListApiService", request.page, request.page_size],
    () => CompanyAffiliationListApiService(request),
  );

// --- Available Fields ---

export const McmAvailableFieldsApiService = async (
  request: McmAvailableFieldsRequest,
): Promise<McmAvailableField[]> => {
  try {
    const url = "/mcm-segments/available-fields/";
    const { data } = await axios.get(url, { params: request });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAvailableFieldsApiService = (
  request: McmAvailableFieldsRequest,
  enabled = true,
) =>
  useQuery<McmAvailableField[], Error | AxiosError>(
    [
      "McmAvailableFieldsApiService",
      request.employer,
      request.target_company,
    ],
    () => McmAvailableFieldsApiService(request),
    { staleTime: 5 * 60 * 1000, enabled, keepPreviousData: true },
  );

// --- Audit Log ---

export const McmAuditLogApiService = async (
  request: McmAuditLogRequest,
): Promise<McmAuditLogWrapper> => {
  try {
    const url = "/mcm-audit-log/";
    const { data } = await axios.get(url, { params: request });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAuditLogApiService = (request: McmAuditLogRequest) =>
  useQuery<McmAuditLogWrapper, Error | AxiosError>(
    [
      "McmAuditLogApiService",
      request.page,
      request.page_size,
      request.action,
    ],
    () => McmAuditLogApiService(request),
    { keepPreviousData: true, refetchOnMount: "always" },
  );
